import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { parse, addDays, addMonths, addYears, isAfter, format } from 'date-fns';
import CarouselLightbox from '../components/CarouselLightbox';
import { getCdnUrl } from '../utils/cdnConfig';
import BlackLogoTextSBS from '../assets/logo/Black Logo + Text SBS.png';
import CommentModal from '../components/CommentModal';
import ReportCommentModal from '../components/ReportCommentModal';
import './Profile.css';
import { getVisitSessionId } from '../services/visitTrackingService';

const PROFILE_LANGUAGE_BY_CODE = {
  mk: 'MACEDONIAN',
  al: 'ALBANIAN',
  tr: 'TURKISH',
  en: 'ENGLISH',
  de: 'GERMAN',
  sr: 'SERBIAN',
  hr: 'CROATIAN'
};

const SHARE_LANGUAGE_OPTIONS = [
  { code: 'mk', icon: 'https://flagcdn.com/w20/mk.png' },
  { code: 'al', icon: 'https://flagcdn.com/w20/al.png' },
  { code: 'en', icon: 'https://flagcdn.com/w20/us.png' },
  { code: 'tr', icon: 'https://flagcdn.com/w20/tr.png' },
  { code: 'hr', icon: 'https://flagcdn.com/w20/hr.png' },
  { code: 'sr', icon: 'https://flagcdn.com/w20/rs.png' },
  { code: 'de', icon: 'https://flagcdn.com/w20/de.png' }
];

// ─── InlineGalleryCarousel ────────────────────────────────────────────────────
/**
 * Inline single-image carousel shown in the gallery section on both mobile and desktop.
 * Supports swipe/touch, keyboard-style prev/next arrows, and dot navigation.
 * Clicking the image opens the full-screen lightbox.
 */
const InlineGalleryCarousel = ({ images, profileName, onImageClick, imageLoadingStates, onImageLoad, t }) => {
  const [current, setCurrent] = useState(0);
  const [touchStartX, setTouchStartX] = useState(null);

  const total = images.length;

  // Preload the next 3 and previous 1 images for smooth navigation
  useEffect(() => {
    const preloadOffsets = [1, 2, 3, -1];
    preloadOffsets.forEach((offset) => {
      const idx = current + offset;
      if (idx >= 0 && idx < total) {
        const url = images[idx]?.url;
        if (url) {
          const img = new Image();
          img.src = getCdnUrl(url);
        }
      }
    });
  }, [current, images, total]);
  const goPrev = () => setCurrent(i => Math.max(0, i - 1));
  const goNext = () => setCurrent(i => Math.min(total - 1, i + 1));

  const handleTouchStart = (e) => setTouchStartX(e.touches[0].clientX);
  const handleTouchEnd = (e) => {
    if (touchStartX === null) return;
    const diff = touchStartX - e.changedTouches[0].clientX;
    if (diff > 50) goNext();
    else if (diff < -50) goPrev();
    setTouchStartX(null);
  };

  const image = images[current];

  return (
    <div className="relative max-w-2xl mx-auto select-none">
      {/* Image wrapper */}
      <div
        className="relative bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden cursor-pointer"
        style={{ minHeight: '300px' }}
        onClick={() => onImageClick(current)}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Loading skeleton — preserves existing loading logic */}
        {!imageLoadingStates[image.id] && (
          <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center rounded-lg">
            <svg className="w-12 h-12 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
            </svg>
          </div>
        )}
        <img
          src={getCdnUrl(image.url)}
          alt={t('profile.galleryImage', '{{name}} - gallery image {{number}}', { name: profileName, number: current + 1 })}
          className={`w-full h-auto max-h-[500px] md:max-h-[600px] object-contain block transition-opacity duration-300 ${
            imageLoadingStates[image.id] ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={() => onImageLoad(image.id)}
          draggable={false}
        />
        {/* Counter badge */}
        {total > 1 && (
          <div className="absolute bottom-3 right-3 bg-black/50 text-white text-xs px-2.5 py-1 rounded-full tabular-nums pointer-events-none">
            {current + 1} / {total}
          </div>
        )}
      </div>

      {/* Prev / Next arrows */}
      {total > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            disabled={current === 0}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white border border-gray-200 rounded-full flex items-center justify-center shadow transition-all disabled:opacity-30 disabled:cursor-not-allowed z-10"
            aria-label={t('profile.previousImage', 'Previous image')}
          >
            <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            disabled={current === total - 1}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white border border-gray-200 rounded-full flex items-center justify-center shadow transition-all disabled:opacity-30 disabled:cursor-not-allowed z-10"
            aria-label={t('profile.nextImage', 'Next image')}
          >
            <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </>
      )}

      {/* Dots — only for manageable counts */}
      {total > 1 && total <= 15 && (
        <div className="flex justify-center gap-1.5 mt-4">
          {images.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              className={`transition-all rounded-full ${
                i === current
                  ? 'w-3 h-3 bg-gray-700'
                  : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'
              }`}
              aria-label={t('profile.goToImage', 'Go to image {{number}}', { number: i + 1 })}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── GalleryGridModal ────────────────────────────────────────────────────────
/**
 * Full-screen thumbnail grid overlay.
 * Clicking a thumbnail fires onThumbClick(index) to open the lightbox.
 */
const GalleryGridModal = ({ images, profileName, onThumbClick, onClose, t }) => {
  // Lock body scroll while open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  return (
    <div
      className="fixed inset-0 z-[9998] bg-black/80 backdrop-blur-sm flex flex-col"
      onClick={onClose}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-6 py-4 bg-black/60"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="text-white font-light text-lg">{profileName}</span>
        <button
          onClick={onClose}
          className="text-white/70 hover:text-white p-2 hover:bg-white/10 rounded-lg transition-colors"
          aria-label={t('profile.closeGallery', 'Close gallery')}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Thumbnail grid */}
      <div
        className="flex-1 overflow-y-auto p-4 md:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2 max-w-6xl mx-auto">
          {images.map((image, index) => (
            <button
              key={image.id}
              className="aspect-square overflow-hidden rounded-md bg-gray-800 hover:ring-2 hover:ring-white/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-white transition-all"
              onClick={() => onThumbClick(index)}
              aria-label={t('profile.openImage', 'Open image {{number}}', { number: index + 1 })}
            >
              <img
                src={getCdnUrl(image.url)}
                alt={`${profileName} - ${index + 1}`}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const Profile = () => {
  const { id: stringId, lang } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [carouselState, setCarouselState] = useState({ isOpen: false, images: [], startIndex: 0 });
  const [imageLoadingStates, setImageLoadingStates] = useState({});
  const [galleryGridOpen, setGalleryGridOpen] = useState(false);
  const hasRecordedQrScanRef = useRef(false); // Track if QR scan was already recorded
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [shareLanguage, setShareLanguage] = useState(lang || 'mk');
  const [isShareLanguageMenuOpen, setIsShareLanguageMenuOpen] = useState(false);
  const [shareUrlCopied, setShareUrlCopied] = useState(false);
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [reportingCommentId, setReportingCommentId] = useState(null); // commentId being reported
  const [reportedCommentIds, setReportedCommentIds] = useState(new Set()); // already reported
  const [reportModalCommentId, setReportModalCommentId] = useState(null); // modal open for this comment
  const [reportModalSubmitting, setReportModalSubmitting] = useState(false);
  const [reportModalSuccess, setReportModalSuccess] = useState(false);
  const [isProfileReportOpen, setIsProfileReportOpen] = useState(false);
  const [profileReportSubmitting, setProfileReportSubmitting] = useState(false);
  const [profileReportSuccess, setProfileReportSuccess] = useState(false);

  const selectedProfileLanguage = PROFILE_LANGUAGE_BY_CODE[lang || i18n.language];
  const selectedTranslation = profile?.translations?.[selectedProfileLanguage];
  const displayedProfile = profile
    ? {
        ...profile,
        introduction: selectedTranslation?.introduction ?? profile.introduction,
        lifeStory: selectedTranslation?.lifeStory ?? profile.lifeStory
      }
    : null;

  // Determine profile image URL
  const profileImageUrl = profile && profile.uploadedImages && profile.uploadedImages.length > 0
    ? getCdnUrl(profile.uploadedImages[0].url)
    : '/assets/placeholder-profile.jpg';

  // Calculate memorial dates (zadushnici) from death date
  const calculateMemorialDates = (deathDateStr) => {
    if (!deathDateStr) return [];
    
    try {
      // Parse DD.MM.YYYY format
      const deathDate = parse(deathDateStr, 'dd.MM.yyyy', new Date());
      if (isNaN(deathDate)) return [];
      
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time for accurate date comparison
      
      // Calculate time since death
      const oneYearFromDeath = addYears(deathDate, 1);
      const isFirstYear = today < oneYearFromDeath;
      
      // Calculate which anniversary year we're in
      const yearsSinceDeath = Math.floor((today - deathDate) / (365.25 * 24 * 60 * 60 * 1000));
      const nextAnniversaryYear = yearsSinceDeath + 1;
      
      const memorialPeriods = [
        { days: 3, label: 'profile.memorial.3days', labelDefault: '3 дена' },
        { days: 7, label: 'profile.memorial.7days', labelDefault: '7 дена' },
        { days: 9, label: 'profile.memorial.9days', labelDefault: '9 дена' },
        { days: 40, label: 'profile.memorial.40days', labelDefault: '40 дена' },
        { months: 3, label: 'profile.memorial.3months', labelDefault: '3 месеци' },
        { months: 6, label: 'profile.memorial.6months', labelDefault: '6 месеци' },
        { years: 1, label: 'profile.memorial.1year', labelDefault: '1 година' }
      ];
      
      let memorials = [];
      
      if (isFirstYear) {
        // First year: show all memorial dates (including expired ones)
        memorials = memorialPeriods.map(period => {
          let memorialDate;
          if (period.days) {
            memorialDate = addDays(deathDate, period.days);
          } else if (period.months) {
            memorialDate = addMonths(deathDate, period.months);
          } else if (period.years) {
            memorialDate = addYears(deathDate, period.years);
          }
          
          const isPast = memorialDate < today;
          
          return {
            date: memorialDate,
            dateString: format(memorialDate, 'dd.MM.yyyy'),
            label: period.label,
            labelDefault: period.labelDefault,
            isPast: isPast,
            isAnniversary: false
          };
        });
      } else {
        // After first year: only show the anniversary date
        const nextAnniversaryDate = addYears(deathDate, nextAnniversaryYear);
        const isPastAnniversary = nextAnniversaryDate < today;
        
        memorials = [{
          date: nextAnniversaryDate,
          dateString: format(nextAnniversaryDate, 'dd.MM.yyyy'),
          label: 'profile.memorial.anniversary',
          labelDefault: 'Годишнина',
          isPast: isPastAnniversary,
          isAnniversary: true,
          anniversaryYear: nextAnniversaryYear
        }];
      }
      
      return memorials;
    } catch (error) {
      console.error('Error calculating memorial dates:', error);
      return [];
    }
  };

  const memorialDates = profile?.deathDate ? calculateMemorialDates(profile.deathDate) : [];

  // Fetch profile data (separated from language logic)
  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      setError(null);
      try {
        // Get QR code from URL query parameters if present
        const searchParams = new URLSearchParams(location.search);
        const qrCode = searchParams.get('qr');
        
        // Only include QR parameter if we haven't recorded it yet
        const shouldTrackQr = qrCode && !hasRecordedQrScanRef.current;
        
        // CRITICAL: Mark as recorded IMMEDIATELY before fetch to prevent double-tracking in React StrictMode
        if (shouldTrackQr) {
          hasRecordedQrScanRef.current = true;
        }
        
        // Build URL with optional QR parameter for tracking
        const url = shouldTrackQr
          ? `/api/profiles/${stringId}?qr=${encodeURIComponent(qrCode)}`
          : `/api/profiles/${stringId}`;
        
        const response = await fetch(url, {
          headers: {
            'X-Visit-Session-Id': getVisitSessionId(),
            'X-Visit-Referrer': document.referrer || ''
          }
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        
        // Parse coordinates string into lat/lng if present
        if (data.coordinates && typeof data.coordinates === 'string') {
          const [lat, lng] = data.coordinates.split(',').map(coord => parseFloat(coord.trim()));
          if (!isNaN(lat) && !isNaN(lng)) {
            data.restingPlaceLat = lat;
            data.restingPlaceLng = lng;
          }
        }
        
        setProfile(data);
        
        // Remove QR parameter from URL to prevent duplicate tracking on re-renders
        if (shouldTrackQr) {
          const newSearchParams = new URLSearchParams(location.search);
          newSearchParams.delete('qr');
          const newSearch = newSearchParams.toString();
          const newUrl = `${location.pathname}${newSearch ? '?' + newSearch : ''}`;
          window.history.replaceState({}, '', newUrl);
        }
      } catch (error) {
        console.error('Error loading profile:', error);
        setError('FETCH_ERROR');
      } finally {
        setLoading(false);
      }
    };

    if (stringId) {
      fetchProfile();
    }
  }, [stringId, location.search]);

  // Set page title and meta tags when profile loads
  useEffect(() => {
    if (displayedProfile) {
      // Set page title
      document.title = `${displayedProfile.name} - Spomen`;

      // Set meta description
      const metaDescription = document.querySelector('meta[name="description"]') || document.createElement('meta');
      metaDescription.name = 'description';
      metaDescription.content = displayedProfile.introduction || `Memorial page for ${displayedProfile.name}`;
      if (!document.querySelector('meta[name="description"]')) {
        document.head.appendChild(metaDescription);
      }

      // Set Open Graph tags for social media previews
      const ogTitle = document.querySelector('meta[property="og:title"]') || document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      ogTitle.content = `${displayedProfile.name} - Memorial`;
      if (!document.querySelector('meta[property="og:title"]')) {
        document.head.appendChild(ogTitle);
      }

      const ogDescription = document.querySelector('meta[property="og:description"]') || document.createElement('meta');
      ogDescription.setAttribute('property', 'og:description');
      ogDescription.content = displayedProfile.introduction || `In loving memory of ${displayedProfile.name}`;
      if (!document.querySelector('meta[property="og:description"]')) {
        document.head.appendChild(ogDescription);
      }

      const ogImage = document.querySelector('meta[property="og:image"]') || document.createElement('meta');
      ogImage.setAttribute('property', 'og:image');
      ogImage.content = profileImageUrl !== '/assets/placeholder-profile.jpg' ? profileImageUrl : '';
      if (!document.querySelector('meta[property="og:image"]')) {
        document.head.appendChild(ogImage);
      }

      const ogUrl = document.querySelector('meta[property="og:url"]') || document.createElement('meta');
      ogUrl.setAttribute('property', 'og:url');
      ogUrl.content = window.location.href;
      if (!document.querySelector('meta[property="og:url"]')) {
        document.head.appendChild(ogUrl);
      }

      // Set Twitter Card tags
      const twitterCard = document.querySelector('meta[name="twitter:card"]') || document.createElement('meta');
      twitterCard.name = 'twitter:card';
      twitterCard.content = 'summary_large_image';
      if (!document.querySelector('meta[name="twitter:card"]')) {
        document.head.appendChild(twitterCard);
      }
    }

    // Cleanup function to reset title when component unmounts
    return () => {
      document.title = 'Spomen';
    };
  }, [displayedProfile?.name, displayedProfile?.introduction, profileImageUrl]);

  const openImageCarousel = (images, startIndex = 0) => {
    setCarouselState({ isOpen: true, images: images, startIndex: startIndex });
  };

  const closeImageCarousel = () => {
    setCarouselState({ isOpen: false, images: [], startIndex: 0 });
  };

  const handleImageLoad = (imageId) => {
    setImageLoadingStates(prev => ({ ...prev, [imageId]: true }));
  };

  // Handle language change (independent of any sessions/cookies)
  const handleLanguageChange = (newLangCode) => {
    i18n.changeLanguage(newLangCode);
    
    // Update URL to reflect the new language
    const currentPath = location.pathname;
    const pathWithoutLang = currentPath.replace(/^\/(mk|al|en|tr|hr|sr|de)/, '');
    navigate(`/${newLangCode}${pathWithoutLang}`, { replace: true });
  };

  // Handle share button click
  const handleShareClick = () => {
    setShareLanguage(lang || 'mk');
    setIsShareLanguageMenuOpen(false);
    setIsShareModalOpen(true);
    setShareUrlCopied(false);
  };

  const closeShareModal = () => {
    setIsShareModalOpen(false);
    setIsShareLanguageMenuOpen(false);
    setShareUrlCopied(false);
  };

  const shareUrl = `https://spomen.mk/${shareLanguage}/profile/${stringId}`;
  const selectedShareLanguage = SHARE_LANGUAGE_OPTIONS.find(
    (language) => language.code === shareLanguage
  ) || SHARE_LANGUAGE_OPTIONS[0];

  // Handle copy URL to clipboard
  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareUrlCopied(true);
      setTimeout(() => setShareUrlCopied(false), 3000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setShareUrlCopied(true);
      setTimeout(() => setShareUrlCopied(false), 3000);
    }
  };

  const handleCommentAdded = (newComment) => {
    setProfile(prev => ({
      ...prev,
      comments: [newComment, ...(prev.comments || [])]
    }));
  };

  const handleOpenReportModal = (commentId) => {
    if (reportedCommentIds.has(commentId)) return;
    setReportModalCommentId(commentId);
    setReportModalSuccess(false);
  };

  const handleCloseReportModal = () => {
    if (reportModalSubmitting) return;
    setReportModalCommentId(null);
    setReportModalSuccess(false);
  };

  const handleSubmitReport = async (reason, captchaToken) => {
    if (!reportModalCommentId) return;
    setReportModalSubmitting(true);
    setReportingCommentId(reportModalCommentId);
    try {
      const response = await fetch(`/api/profiles/${profile.stringId}/comments/${reportModalCommentId}/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason, captchaToken }),
      });
      if (response.ok) {
        setReportedCommentIds(prev => new Set([...prev, reportModalCommentId]));
        setReportModalSuccess(true);
      }
    } catch (err) {
      console.error('Failed to report comment:', err);
    } finally {
      setReportModalSubmitting(false);
      setReportingCommentId(null);
    }
  };

  const handleCloseProfileReport = () => {
    if (profileReportSubmitting) return;
    setIsProfileReportOpen(false);
    setProfileReportSuccess(false);
  };

  const handleSubmitProfileReport = async (reason, captchaToken) => {
    setProfileReportSubmitting(true);
    try {
      const response = await fetch(`/api/profiles/${profile.stringId}/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason, captchaToken }),
      });
      if (response.ok) {
        setProfileReportSuccess(true);
      }
    } catch (err) {
      console.error('Failed to report profile:', err);
    } finally {
      setProfileReportSubmitting(false);
    }
  };

  if (loading) {
    return <div className="loading">{t('profile.loading')}</div>;
  }

  if (error) {
    return <div className="error">{error === 'FETCH_ERROR' ? t('profile.errorLoading') : error}</div>;
  }

  if (!profile) {
    return <div className="error">{t('profile.profileNotFound')}</div>;
  }

  // Determine profile image and gallery images - moved up to avoid reference error
  const galleryImages = profile.uploadedImages && profile.uploadedImages.length > 1
    ? profile.uploadedImages.slice(1)
    : [];

  // Gallery images count
  const numGalleryImages = galleryImages.length;

  return (
    <>
      <div className="profile-memorial min-h-screen bg-gray-50">

        {/* Logo */}
        <div className="absolute top-6 left-6 z-50">
          <div className="bg-white px-4 py-2 rounded-lg shadow-sm border border-gray-100 h-12 flex items-center cursor-pointer hover:shadow-md transition-shadow duration-200"
               onClick={() => navigate(`/${i18n.language}/`)}>
            <img src={BlackLogoTextSBS} alt="Spomen Logo" className="h-8 w-auto" />
          </div>
        </div>

        {/* Language Picker */}
        <div className="absolute top-6 right-6 z-50">
          <select
            value={i18n.language}
            onChange={(e) => handleLanguageChange(e.target.value)}
            className="bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 shadow-sm hover:shadow-md transition-shadow duration-200 focus:outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400 h-12"
            style={{
              appearance: 'none',
              WebkitAppearance: 'none',
              MozAppearance: 'none',
              backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%23666\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6,9 12,15 18,9\'%3e%3c/polyline%3e%3c/svg%3e")',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 8px center',
              backgroundSize: '16px',
              paddingRight: '32px'
            }}
          >
            <option value="mk">{t('languages.macedonian', 'Македонски')}</option>
            <option value="al">{t('languages.albanian', 'Shqip')}</option>
            <option value="tr">{t('languages.turkish', 'Türkçe')}</option>
            <option value="en">{t('languages.english', 'English')}</option>
            <option value="de">{t('languages.german', 'Deutsch')}</option>
            <option value="sr">{t('languages.serbian', 'Српски')}</option>
            <option value="hr">{t('languages.croatian', 'Hrvatski')}</option>
          </select>
        </div>

        {/* Profile Header Section */}
        <section className="profile-header-section pt-24 pb-16">
          <div className="container mx-auto px-6">
            <div className="max-w-5xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-start">
                
                {/* Profile Image - Desktop */}
                <div className="order-2 lg:order-1 hidden lg:block">
                  <div className="relative">
                    <div className="profile-image-main-container bg-white p-1 rounded-lg shadow-md border border-gray-200">
                      <div className="aspect-square overflow-hidden rounded-md bg-gray-100">
                        {!imageLoadingStates[profileImageUrl] && (
                          <div className="w-full h-full bg-gray-200 animate-pulse flex items-center justify-center">
                            <svg className="w-12 h-12 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                        <img
                          src={profileImageUrl}
                          alt={displayedProfile.name}
                          className={`w-full h-full object-cover cursor-pointer transition-opacity duration-300 ${
                            imageLoadingStates[profileImageUrl] ? 'opacity-100' : 'opacity-0 absolute'
                          }`}
                          onClick={() => profileImageUrl !== '/assets/placeholder-profile.jpg' && openImageCarousel([{url: profileImageUrl}])}
                          onLoad={() => handleImageLoad(profileImageUrl)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Profile Information */}
                <div className="order-1 lg:order-2 text-center lg:text-left">
                  <div className="inline-flex items-center bg-gray-100 text-gray-600 px-3 py-1 rounded-md text-sm mb-6">
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    {t('profile.inMemoryOf')}
                  </div>
                  
                  <h1 className="text-4xl lg:text-6xl font-light text-gray-900 mb-2 leading-tight">
                    {displayedProfile.name}
                  </h1>

                  {selectedTranslation && !selectedTranslation.machineTranslated && (
                    <p
                      className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
                      role="status"
                    >
                      {t('profile.translationUnavailable')}
                    </p>
                  )}
                  
                  {/* Birth and Death Dates */}
                  <div className="mb-6">
                    <p className="text-2xl lg:text-3xl font-light text-gray-600 mb-4">
                      {profile.birthDate || '01.01.1990'} - {profile.deathDate || '01.01.2099'}
                    </p>
                    {(profile.birthDetails || profile.deathDetails) && (
                      <div className="bg-white rounded-lg px-4 py-2 shadow-sm border border-gray-200 inline-block">
                        <p className="text-lg text-gray-600">
                          {profile.birthDetails && profile.deathDetails 
                            ? `${profile.birthDetails} - ${profile.deathDetails}`
                            : profile.birthDetails || profile.deathDetails
                          }
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {/* Profile Image - Mobile */}
                  <div className="lg:hidden mb-6">
                    <div className="relative max-w-xs mx-auto">
                      <div className="profile-image-main-container bg-white p-1 rounded-lg shadow-md border border-gray-200">
                        <div className="aspect-square overflow-hidden rounded-md bg-gray-100">
                          {!imageLoadingStates[profileImageUrl] && (
                            <div className="w-full h-full bg-gray-200 animate-pulse flex items-center justify-center">
                              <svg className="w-12 h-12 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                          <img
                            src={profileImageUrl}
                            alt={displayedProfile.name}
                            className={`w-full h-full object-cover cursor-pointer transition-opacity duration-300 ${
                              imageLoadingStates[profileImageUrl] ? 'opacity-100' : 'opacity-0 absolute'
                            }`}
                            onClick={() => profileImageUrl !== '/assets/placeholder-profile.jpg' && openImageCarousel([{url: profileImageUrl}])}
                            onLoad={() => handleImageLoad(profileImageUrl)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {displayedProfile.introduction && (
                    <p className="text-lg text-gray-700 leading-relaxed mb-8 max-w-2xl text-justify">
                      {displayedProfile.introduction}
                    </p>
                  )}
                  
                  {/* Share Button */}
                  <button 
                    onClick={handleShareClick}
                    className="inline-flex items-center justify-center px-6 py-3 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200 shadow-sm"
                  >
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
                    </svg>
                    {t('profile.share')}
                  </button>
                </div>
                
              </div>
            </div>
          </div>
        </section>

        {/* Gallery Section */}
        {galleryImages.length > 0 && (
          <section className="gallery-section py-16 bg-white">
            <div className="container mx-auto px-6">
              
              {/* Section Header */}
              <div className="text-center mb-12">
                <div className="inline-flex items-center bg-gray-100 text-gray-600 px-4 py-2 rounded-md text-sm mb-4">
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                  </svg>
                  {t('profile.galleryTitle', 'Gallery')}
                </div>
                <h2 className="text-3xl font-light text-gray-900 mb-3">
                  {t('profile.memoriesTitle', 'Cherished Memories')}
                </h2>
                <p className="text-gray-600 max-w-xl mx-auto">
                  {t('profile.memoriesSubtitle', 'A collection of beautiful moments and memories')}
                </p>
              </div>

              {/* Gallery Carousel — single-image carousel on both mobile and desktop */}
              <div className="max-w-6xl mx-auto">
                <InlineGalleryCarousel
                  images={galleryImages}
                  profileName={displayedProfile.name}
                  onImageClick={(index) => openImageCarousel(galleryImages, index)}
                  imageLoadingStates={imageLoadingStates}
                  onImageLoad={handleImageLoad}
                  t={t}
                />

                {/* Gallery Stats — click to open thumbnail grid */}
                <div className="text-center mt-10">
                  <button
                    onClick={() => setGalleryGridOpen(true)}
                    className="inline-flex items-center bg-white rounded-lg px-4 py-2 shadow-sm border border-gray-200 hover:shadow-md hover:border-gray-300 transition-all cursor-pointer"
                  >
                    <svg className="w-4 h-4 text-gray-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-600 text-sm">
                      {numGalleryImages} {numGalleryImages === 1 ? t('profile.photo') : t('profile.photos')}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Gallery Grid Modal */}
        {galleryGridOpen && (
          <GalleryGridModal
            images={galleryImages}
            profileName={displayedProfile.name}
            onClose={() => setGalleryGridOpen(false)}
            t={t}
            onThumbClick={(index) => {
              setGalleryGridOpen(false);
              openImageCarousel(galleryImages, index);
            }}
          />
        )}

        {/* Lightbox Modal */}
        <CarouselLightbox
          images={carouselState.images}
          initialIndex={carouselState.startIndex}
          isOpen={carouselState.isOpen}
          onClose={closeImageCarousel}
          profileName={displayedProfile?.name}
        />

        {/* Life Story Section */}
        {displayedProfile.lifeStory && (
          <section className="life-section py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl"></div>
              <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500 rounded-full filter blur-3xl"></div>
            </div>

            <div className="container mx-auto px-6 relative z-10">
              <div className="max-w-6xl mx-auto">
                
                {/* Section Header */}
                <div className="text-center mb-16">
                  <div className="inline-flex items-center bg-white/10 backdrop-blur-sm text-white/90 px-5 py-2.5 rounded-full text-sm mb-6 shadow-lg border border-white/20">
                    <svg className="w-5 h-5 mr-2.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clipRule="evenodd" />
                    </svg>
                    <span className="font-semibold">{t('profile.life', 'Животна Приказна')}</span>
                  </div>
                  <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                    {t('profile.lifeStoryTitle', 'Живот Во Спомени')}
                  </h2>
                  <div className="w-24 h-1.5 bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 mx-auto rounded-full"></div>
                </div>

                {/* Life Story Content */}
                <div className="bg-white/95 backdrop-blur-md p-8 md:p-12 lg:p-16 rounded-3xl shadow-2xl border border-white/20">
                  <div className="prose prose-lg lg:prose-xl max-w-none">
                    <div className="flex flex-col lg:flex-row gap-12 items-start">
                      
                      {/* Life Image */}
                      {profile.lifeImage && (
                        <div className="flex-shrink-0 lg:w-80">
                          <div className="relative group">
                            <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-2xl blur opacity-25 group-hover:opacity-40 transition duration-500"></div>
                            <div className="relative bg-white p-2 rounded-2xl shadow-xl">
                              <div className="aspect-square overflow-hidden rounded-xl">
                                <img
                                  src={getCdnUrl(profile.lifeImage || '/assets/placeholder-life.jpg')}
                                  alt={`${displayedProfile.name} - Life`}
                                  className="w-full h-full object-cover transform group-hover:scale-105 transition duration-500"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Life Story Text */}
                      <div className="flex-1">
                        <div className="relative">
                          {/* Quote decoration */}
                          <div className="absolute -left-4 -top-4 text-8xl text-slate-200 font-serif leading-none">"</div>
                          <p className="text-slate-700 leading-relaxed text-lg md:text-xl font-light relative z-10 first-letter:text-7xl first-letter:font-bold first-letter:text-slate-900 first-letter:float-left first-letter:mr-3 first-letter:leading-none">
                            {displayedProfile.lifeStory}
                          </p>
                          <div className="absolute -right-4 -bottom-4 text-8xl text-slate-200 font-serif leading-none">"</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}
        
        {/* Meta Information Section */}
        <section className="meta-section py-20 bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50 relative overflow-hidden">
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-6xl mx-auto">
              
              {/* Section Header */}
              <div className="text-center mb-16">
                <div className="inline-flex items-center bg-white/90 backdrop-blur-sm text-slate-600 px-6 py-3 rounded-full text-sm mb-6 shadow-md border border-slate-300/50">
                  <svg className="w-5 h-5 mr-2.5 text-slate-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                  </svg>
                  <span className="font-bold">{t('profile.lifeDetails', 'Детали за Животот')}</span>
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">
                  {t('profile.importantDates', 'Важни Датуми и Места')}
                </h2>
                <div className="w-24 h-1 bg-slate-400 mx-auto rounded-full"></div>
              </div>

              {/* Meta Cards - Landscape Mode */}
              <div className="space-y-6">

                {/* Born Card - Landscape */}
                {(profile.birthDate || displayedProfile.birthPlace) && (
                  <div className="group relative bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 overflow-hidden border border-teal-100/50">
                    {/* Gradient accent bar */}
                    <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b from-teal-700 to-emerald-800"></div>
                    
                    <div className="flex items-start p-6 md:p-8">
                      {/* Icon Section */}
                      <div className="flex-shrink-0 mr-6 md:mr-8">
                        <div className="w-20 h-20 md:w-24 md:h-24 bg-gradient-to-br from-teal-700 to-emerald-800 rounded-2xl flex items-center justify-center shadow-md shadow-teal-900/20 group-hover:scale-105 transition-all duration-500">
                          <svg className="w-10 h-10 md:w-12 md:h-12 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                      
                      {/* Content Section */}
                      <div className="flex-1 min-w-0">
                        <div className="mb-4">
                          <h3 className="text-2xl md:text-3xl font-bold text-teal-800 uppercase tracking-wide">
                            {t('profile.born', 'Роден')}
                          </h3>
                        </div>
                        
                        <div className="flex flex-wrap gap-6 md:gap-8">
                          {/* Date */}
                          {profile.birthDate && (
                            <div className="flex items-center space-x-3">
                              <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center">
                                <svg className="w-6 h-6 text-teal-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                              </div>
                              <div>
                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{t('profile.date', 'Датум')}</p>
                                <p className="text-xl md:text-2xl font-bold text-slate-800">{profile.birthDate}</p>
                              </div>
                            </div>
                          )}
                          
                          {/* Place */}
                          {displayedProfile.birthPlace && (
                            <div className="flex items-center space-x-3">
                              <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center">
                                <svg className="w-6 h-6 text-teal-700" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <div>
                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{t('profile.place', 'Место')}</p>
                                <p className="text-lg md:text-xl font-bold text-slate-800">{displayedProfile.birthPlace}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Died Card - Landscape */}
                {(profile.deathDate || displayedProfile.deathPlace) && (
                  <div className="group relative bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 overflow-hidden border border-amber-100/50">
                    {/* Gradient accent bar */}
                    <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b from-amber-700 to-orange-800"></div>
                    
                    <div className="flex items-start p-6 md:p-8">
                      {/* Icon Section */}
                      <div className="flex-shrink-0 mr-6 md:mr-8">
                        <div className="w-20 h-20 md:w-24 md:h-24 bg-gradient-to-br from-amber-700 to-orange-800 rounded-2xl flex items-center justify-center shadow-md shadow-amber-900/20 group-hover:scale-105 transition-all duration-500">
                          <svg className="w-10 h-10 md:w-12 md:h-12 text-white" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12,2L8,6V8H6V22H18V8H16V6L12,2M10,7H14V8H10V7M8,10H16V20H8V10M10,12V18H14V12H10M11,13H13V17H11V13Z"/>
                            <path d="M12,4L13.5,5.5H10.5L12,4Z"/>
                          </svg>
                        </div>
                      </div>
                      
                      {/* Content Section */}
                      <div className="flex-1 min-w-0">
                        <div className="mb-4">
                          <h3 className="text-2xl md:text-3xl font-bold text-amber-800 uppercase tracking-wide">
                            {t('profile.died', 'Починал')}
                          </h3>
                        </div>
                        
                        <div className="flex flex-wrap gap-6 md:gap-8">
                          {/* Date */}
                          {profile.deathDate && (
                            <div className="flex items-center space-x-3">
                              <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center flex-shrink-0">
                                <svg className="w-6 h-6 text-amber-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                              </div>
                              <div>
                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{t('profile.date', 'Датум')}</p>
                                <p className="text-xl md:text-2xl font-bold text-slate-800">{profile.deathDate}</p>
                              </div>
                            </div>
                          )}
                          
                          {/* Place */}
                          {displayedProfile.deathPlace && (
                            <div className="flex items-center space-x-3">
                              <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center flex-shrink-0">
                                <svg className="w-6 h-6 text-amber-700" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <div>
                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{t('profile.place', 'Место')}</p>
                                <p className="text-lg md:text-xl font-bold text-slate-800">{displayedProfile.deathPlace}</p>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Memorial Dates */}
                        {memorialDates.length > 0 && (
                          <div className="mt-6 space-y-3">
                            <h4 className="text-sm font-semibold text-amber-900 uppercase tracking-wide mb-3">{t('profile.memorialDates', 'Помени')}</h4>
                            <div className="space-y-2">
                              {memorialDates.map((memorial, index) => (
                                <div 
                                  key={index}
                                  className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                                    memorial.isPast 
                                      ? 'bg-slate-100/50 border-slate-200/50 opacity-60' 
                                      : 'bg-amber-50/50 border-amber-200/50 hover:bg-amber-100/50'
                                  }`}
                                >
                                  <div className="flex items-center space-x-3">
                                    <div className={`w-2 h-2 rounded-full ${memorial.isPast ? 'bg-slate-400' : 'bg-amber-600'}`}></div>
                                    <span className={`text-sm font-medium ${memorial.isPast ? 'text-slate-500' : 'text-slate-700'}`}>
                                      {memorial.isAnniversary ? (
                                        <>
                                          {t(memorial.label, memorial.labelDefault)} - {memorial.anniversaryYear} {t('profile.memorial.years', 'години')}
                                        </>
                                      ) : (
                                        t(memorial.label, memorial.labelDefault)
                                      )}
                                    </span>
                                  </div>
                                  <span className={`text-sm font-bold ${memorial.isPast ? 'text-slate-500' : 'text-amber-800'}`}>
                                    {memorial.dateString}
                                  </span>
                                </div>
                              ))}
                            </div>
                            <p className="text-xs text-slate-500 italic mt-3 leading-relaxed">
                              {t('profile.memorialDatesDisclaimer', 'Помените се математички искалкулирани според календарски датуми само, и може да варираат помеѓу религии и обичаи.')}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Resting Place Card - Landscape */}
                {(displayedProfile.restingPlaceName || displayedProfile.restingPlaceCity) && (
                  <div className="group relative bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 overflow-hidden border border-indigo-100/50">
                    {/* Gradient accent bar */}
                    <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b from-indigo-700 to-violet-800"></div>
                    
                    <div className="flex items-start p-6 md:p-8">
                      {/* Icon Section */}
                      <div className="flex-shrink-0 mr-6 md:mr-8">
                        <div className="w-20 h-20 md:w-24 md:h-24 bg-gradient-to-br from-indigo-700 to-violet-800 rounded-2xl flex items-center justify-center shadow-md shadow-indigo-900/20 group-hover:scale-105 transition-all duration-500">
                          <svg className="w-10 h-10 md:w-12 md:h-12 text-white" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12,2L8,6V8H6V22H18V8H16V6L12,2M10,7H14V8H10V7M8,10H16V20H8V10M10,12V18H14V12H10M11,13H13V17H11V13Z"/>
                            <path d="M12,4L13.5,5.5H10.5L12,4Z"/>
                          </svg>
                        </div>
                      </div>
                      
                      {/* Content Section */}
                      <div className="flex-1 min-w-0">
                        <div className="mb-4">
                          <h3 className="text-2xl md:text-3xl font-bold text-indigo-800 uppercase tracking-wide">
                            {t('profile.restingPlace', 'Место на Починка')}
                          </h3>
                        </div>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-6">
                          {/* Cemetery Name */}
                          {displayedProfile.restingPlaceName && (
                            <div className="flex items-start space-x-3">
                              <div className="w-12 h-12 bg-indigo-50 rounded-xl flex items-center justify-center flex-shrink-0">
                                <svg className="w-6 h-6 text-indigo-700" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{t('profile.cemetery', 'Гробишта')}</p>
                                <a
                                  href={`https://www.google.com/search?q=${encodeURIComponent(displayedProfile.restingPlaceName)}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-base md:text-lg font-bold text-indigo-700 hover:text-indigo-900 underline underline-offset-2 break-words"
                                >{displayedProfile.restingPlaceName}</a>
                              </div>
                            </div>
                          )}
                          
                          {/* City */}
                          {displayedProfile.restingPlaceCity && (
                            <div className="flex items-start space-x-3">
                              <div className="w-12 h-12 bg-indigo-50 rounded-xl flex items-center justify-center flex-shrink-0">
                                <svg className="w-6 h-6 text-indigo-700" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{t('profile.city', 'Град')}</p>
                                <p className="text-base md:text-lg font-bold text-slate-800">
                                  {displayedProfile.restingPlaceCity}
                                  {profile.restingPlacePostalCode && (
                                    <span className="text-sm text-slate-500 font-normal ml-2">({profile.restingPlacePostalCode})</span>
                                  )}
                                </p>
                              </div>
                            </div>
                          )}
                          
                          {/* Country */}
                          {displayedProfile.restingPlaceCountry && (
                            <div className="flex items-start space-x-3">
                              <div className="w-12 h-12 bg-indigo-50 rounded-xl flex items-center justify-center flex-shrink-0">
                                <svg className="w-6 h-6 text-indigo-700" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0 0116 10c0 .34-.028.675-.083 1H15a2 2 0 00-2 2v2.197A5.973 5.973 0 0110 16v-2a2 2 0 00-2-2 2 2 0 01-2-2 2 2 0 00-1.668-1.973z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{t('profile.country', 'Држава')}</p>
                                <p className="text-base md:text-lg font-bold text-slate-800">{displayedProfile.restingPlaceCountry}</p>
                              </div>
                            </div>
                          )}
                          
                          {/* Coordinates */}
                          {profile.restingPlaceLat && profile.restingPlaceLng && (
                            <div className="flex items-start space-x-3 sm:col-span-2 lg:col-span-3">
                              <div className="w-12 h-12 bg-indigo-50 rounded-xl flex items-center justify-center flex-shrink-0">
                                <svg className="w-6 h-6 text-indigo-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                                </svg>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{t('profile.graveLocation', 'Локација на гроб')}</p>
                                <p className="text-sm md:text-base font-mono font-bold text-slate-800 bg-slate-50 px-3 py-2 rounded-lg inline-block">
                                  {profile.restingPlaceLat.toFixed(6)}, {profile.restingPlaceLng.toFixed(6)}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Map Button */}
                        {profile.restingPlaceLat && profile.restingPlaceLng && (
                          <a 
                            className="group/btn inline-flex items-center justify-center bg-gradient-to-r from-indigo-700 to-violet-800 hover:from-indigo-800 hover:to-violet-900 text-white px-6 py-3.5 rounded-xl transition-all duration-300 text-sm md:text-base font-bold shadow-md hover:shadow-lg" 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            href={`https://www.google.com/maps/dir/?api=1&destination=${profile.restingPlaceLat},${profile.restingPlaceLng}`}
                          >
                            <svg className="w-5 h-5 mr-2.5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                            </svg>
                            <span>{t('profile.openInMaps', 'Однеси ме до гробот')}</span>
                            <svg className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                            </svg>
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>
        </section>

        {/* Comments Section */}
        <section className="comments-section py-20 bg-white border-t border-gray-100">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              
              {/* Section Header */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-b border-gray-100 pb-6 mb-8 gap-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-slate-50 rounded-xl flex items-center justify-center border border-slate-100 shadow-sm text-slate-850">
                    <svg className="w-6 h-6 text-slate-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-3xl font-light text-slate-900">
                      {t('profile.commentsSectionTitle', 'Condolences & Memories')}
                    </h2>
                  </div>
                </div>

                <button
                  onClick={() => setIsCommentModalOpen(true)}
                  className="inline-flex items-center justify-center px-5 py-3 text-sm font-semibold text-white bg-slate-900 hover:bg-slate-800 rounded-xl shadow-md transition-all duration-300 transform hover:-translate-y-0.5"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                  </svg>
                  {t('profile.addCommentButton', 'Share a Memory')}
                </button>
              </div>

              {/* Comments List */}
              {!profile.comments || profile.comments.length === 0 ? (
                <div className="text-center py-12 px-6 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                  <p className="text-slate-500 font-light text-base">
                    {t('profile.noComments', 'No messages yet. Be the first to write a memory.')}
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {profile.comments.map((comment) => (
                    <div 
                      key={comment.id}
                      className="bg-slate-50/50 hover:bg-slate-50 border border-slate-100 hover:border-slate-200/85 rounded-2xl p-6 transition-all duration-300 shadow-sm"
                    >
                      <div className="flex items-start justify-between gap-4 mb-3">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-slate-900 text-white rounded-full flex items-center justify-center font-bold text-sm select-none shadow-sm uppercase">
                            {comment.name.substring(0, 2)}
                          </div>
                          <div>
                            <h4 className="text-base font-semibold text-slate-800">
                              {comment.name}
                            </h4>
                            <p className="text-xs text-slate-450 font-medium text-slate-400">
                              {new Date(comment.createdAt).toLocaleDateString(i18n.language, {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                        </div>
                        {/* Report button */}
                        <button
                          onClick={() => handleOpenReportModal(comment.id)}
                          disabled={reportedCommentIds.has(comment.id)}
                          title={reportedCommentIds.has(comment.id) ? t('profile.commentReported', 'Reported') : t('profile.reportComment', 'Report comment')}
                          className={`flex-shrink-0 flex items-center gap-1 text-xs px-2 py-1 rounded-lg border transition-all duration-200 ${
                            reportedCommentIds.has(comment.id)
                              ? 'border-red-200 bg-red-50 text-red-400 cursor-default'
                              : 'border-slate-200 text-slate-400 hover:border-red-300 hover:text-red-500 hover:bg-red-50 cursor-pointer'
                          }`}
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
                          </svg>
                          {reportedCommentIds.has(comment.id)
                            ? t('profile.commentReported', 'Reported')
                            : t('profile.reportComment', 'Report')}
                        </button>
                      </div>

                      <p className="text-slate-605 text-slate-600 font-light text-base leading-relaxed text-justify whitespace-pre-line pl-0 sm:pl-[3.25rem]">
                        {comment.content}
                      </p>
                    </div>
                  ))}
                </div>
              )}

            </div>
          </div>
        </section>

        <section className="py-8 bg-gray-50 border-t border-gray-100">
          <div className="container mx-auto px-6 text-center">
            <p className="text-sm text-slate-500 mb-3">
              {t('profile.reportProfilePrompt', 'Is something inappropriate or incorrect on this profile?')}
            </p>
            <button
              type="button"
              onClick={() => {
                setProfileReportSuccess(false);
                setIsProfileReportOpen(true);
              }}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 border border-red-200 rounded-xl hover:bg-red-50 hover:border-red-300 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2z" />
              </svg>
              {t('profile.reportProfile', 'Report this profile')}
            </button>
          </div>
        </section>
        
        <CommentModal
          isOpen={isCommentModalOpen}
          onClose={() => setIsCommentModalOpen(false)}
          profileStringId={profile.stringId}
          onCommentAdded={handleCommentAdded}
        />

        <ReportCommentModal
          isOpen={!!reportModalCommentId}
          onClose={handleCloseReportModal}
          onSubmit={handleSubmitReport}
          isSubmitting={reportModalSubmitting}
          isSuccess={reportModalSuccess}
        />

        <ReportCommentModal
          subject="profile"
          isOpen={isProfileReportOpen}
          onClose={handleCloseProfileReport}
          onSubmit={handleSubmitProfileReport}
          isSubmitting={profileReportSubmitting}
          isSuccess={profileReportSuccess}
        />

        {isShareModalOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
            onClick={closeShareModal}
            role="presentation"
          >
            <div
              className="relative w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl"
              onClick={(event) => event.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="share-profile-title"
            >
              <button
                type="button"
                onClick={closeShareModal}
                className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
                aria-label={t('common.close', 'Close')}
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <div className="pr-10">
                <h2 id="share-profile-title" className="text-2xl font-semibold text-gray-900">
                  {t('profile.share', 'Share')}
                </h2>
                <p className="mt-2 text-sm text-gray-600">
                  {t('profile.shareLinkDescription', 'Copy the link to this memorial profile.')}
                </p>
              </div>

              <div className="relative mt-6 rounded-xl border border-gray-300 bg-gray-50 shadow-sm focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-100">
                <div className="flex min-w-0 items-center text-sm text-gray-700 sm:text-base">
                  <span className="shrink-0 py-3 pl-4 font-medium">spomen.mk/</span>
                  <div className="relative shrink-0 border-x border-gray-300 bg-white">
                    <button
                      type="button"
                      onClick={() => setIsShareLanguageMenuOpen((isOpen) => !isOpen)}
                      className="flex items-center px-3 py-3 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-100 focus:outline-none"
                      aria-label={t('profile.shareLanguage', 'Language')}
                      aria-haspopup="listbox"
                      aria-expanded={isShareLanguageMenuOpen}
                    >
                      <img
                        src={selectedShareLanguage.icon}
                        alt=""
                        className="mr-2 h-auto w-4 rounded-sm"
                      />
                      <span>{selectedShareLanguage.code.toUpperCase()}</span>
                      <svg
                        className={`ml-2 h-3 w-3 transition-transform ${isShareLanguageMenuOpen ? 'rotate-180' : ''}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {isShareLanguageMenuOpen && (
                      <div
                        className="absolute left-0 top-full z-20 mt-2 w-full min-w-[5.5rem] overflow-hidden rounded-lg bg-white py-1 shadow-lg ring-1 ring-black/5"
                        role="listbox"
                        aria-label={t('profile.shareLanguage', 'Language')}
                      >
                        {SHARE_LANGUAGE_OPTIONS.map((language) => (
                          <button
                            key={language.code}
                            type="button"
                            role="option"
                            aria-selected={shareLanguage === language.code}
                            onClick={() => {
                              setShareLanguage(language.code);
                              setIsShareLanguageMenuOpen(false);
                              setShareUrlCopied(false);
                            }}
                            className={`flex w-full items-center px-3 py-2 text-xs font-semibold transition-colors ${
                              shareLanguage === language.code
                                ? 'bg-[#f8f6f3] text-[#8b5a3c]'
                                : 'text-gray-700 hover:bg-gray-100'
                            }`}
                          >
                            <img src={language.icon} alt="" className="mr-2 h-auto w-4 rounded-sm" />
                            {language.code.toUpperCase()}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <span className="min-w-0 truncate py-3 pr-4 pl-3 font-medium">
                    /profile/{stringId}
                  </span>
                </div>
              </div>

              <div className="mt-4">
                <button
                  type="button"
                  onClick={handleCopyUrl}
                  className="inline-flex w-full items-center justify-center rounded-xl bg-gradient-to-r from-indigo-700 to-violet-800 px-6 py-4 text-base font-bold text-white shadow-md transition-all hover:from-indigo-800 hover:to-violet-900 hover:shadow-lg"
                >
                  {shareUrlCopied
                    ? t('profile.linkCopied', 'Copied!')
                    : t('profile.copyLink', 'Copy')}
                </button>
              </div>
            </div>
          </div>
        )}
        
      </div>
    </>
  );
};

export default Profile;
